// 类型详情（管理员，后台规范 §3.3）：顶部浅蓝块（返回 · 标题 · 右上角成熟度 + 启用 / 停用 · 一句话现状 · 可用的操作 · tab）
// 启用和成熟度只在顶部改、立即生效（2026-10-30 业主定：不要到下面设置里去选）；
// tab：设置（字段/文件/检查点/完成证据/测试 manifest）、SOP 编辑、版本历史（回滚/对比）、时间线、AI 分析
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { BrowserKind, CheckpointDef, Maturity, SkillVersionDTO } from "@csp/shared";
import { api, errorText } from "../../lib/api";
import { useAlerts } from "../../lib/alerts";
import { fmtDateTime, fmtPct, fmtTime, MATURITY_TEXT, REVIEWER_TEXT } from "../../lib/format";
import { useAction, useLoad } from "../../lib/hooks";
import type { MarkerItem, TypeDetail } from "../../lib/types";
import { Badge, MarkerBadge } from "../../components/Badges";
import { ColumnPicker, DataTable, useHiddenColumns, type Column } from "../../components/DataTable";
import { DiffView } from "../../components/DiffView";
import { useDialog } from "../../components/Dialogs";
import { cleanSchema, SchemaEditor } from "../../components/SchemaEditor";
import { TimelineChart } from "../../components/TimelineChart";
import { SopSupplements } from "../../components/SopSupplements";
import { CategoryPicker } from "../../components/CategoryPicker";

type Tab = "settings" | "supp" | "sop" | "memory" | "versions" | "timeline" | "ai";
const TABS: [Tab, string][] = [
  ["settings", "设置"],
  ["supp", "SOP 补充"],
  ["sop", "SOP 编辑"],
  ["memory", "操作记忆"],
  ["versions", "版本历史"],
  ["timeline", "时间线"],
  ["ai", "AI 分析"],
];

export function TypeDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const dialog = useDialog();
  const { toast } = useAlerts();
  const typeQ = useLoad(() => api.type(id), [id]);
  const [tab, setTab] = useState<Tab>("settings");
  const act = useAction();
  const t = typeQ.data;

  if (typeQ.error && !t) return <div className="notice err">{typeQ.error}</div>;
  if (!t) return <div className="empty">加载中…</div>;

  const dryRun = async () => {
    const r = await act.run("dry", () => api.dryRun(t.id));
    if (r) navigate(`/tasks/${r.taskId}`);
  };
  const firstRun = async (formal: boolean) => {
    const body = formal
      ? <p>正式探索会<b>真实提交</b>：按页面要求从材料里找资料逐项填写，到提交前停在检查点，等审核批准后才提交。先进入材料准备页面上传文件、填写补充说明，点击「开始运行」才启动浏览器。</p>
      : <p>先进入材料准备页面，可上传多份文件并填写补充说明。确认后点击「开始运行」才会启动浏览器。登录时等你扫码，缺资料时暂停询问。演练到提交前就结束，不会提交。</p>;
    // 测试 / 探索任务也要先起名，然后进全屏「准备文件」
    const named = await dialog.promptEx({ title: formal ? "准备正式探索" : "准备本次探索", body, label: "任务名称", placeholder: "例如：郭书涛 ICA 入境申诉（测试）", required: true, okText: "下一步：准备文件" });
    if (!named) return;
    const r = await act.run("learn", () => api.firstRun(t.id, formal, named.text.trim()));
    if (r) navigate(`/tasks/${r.taskId}/prepare`);
  };
  const canEnable = t.maturity === "L2" || t.maturity === "L3";
  const setEnabled = async (on: boolean) => {
    const ok = await dialog.confirm(
      on
        ? { title: `启用「${t.name}」？`, body: <p>启用后文员在「新建任务」里就能选到它。{t.maturity === "L2" ? "L2：每单都停在最终提交前，等人审核后才提交。" : "L3：按检查点设置，可以自动提交。"}</p>, okText: "启用" }
        : { title: `停用「${t.name}」？`, body: <p>停用后文员不能再新建这个类型的任务；已经在跑的任务不受影响。</p>, okText: "停用", danger: true },
    );
    if (!ok) return;
    if (await act.run("enable", () => api.updateType(t.id, { enabled: on }))) {
      toast("ok", on ? "已启用，文员现在可以新建这个类型的任务" : "已停用");
      void typeQ.reload();
    }
  };
  const setMaturity = async (m: Maturity) => {
    if (m === t.maturity) return;
    const off = t.enabled && (m === "L0" || m === "L1");
    const ok = await dialog.confirm({ title: `成熟度改成 ${MATURITY_TEXT[m]}？`, body: <p>{off ? "L0 / L1 不能启用：改了之后这个类型会同时停用，文员不能再新建它的任务。" : "立即生效。"}</p>, okText: "改", danger: off });
    if (!ok) return;
    if (await act.run("maturity", () => api.updateType(t.id, { maturity: m }))) {
      toast("ok", `成熟度已改成 ${MATURITY_TEXT[m]}`);
      void typeQ.reload();
    }
  };
  /** 一句话现状：文员能不能用、下一步做什么 */
  const state = t.enabled
    ? `已启用：文员在「新建任务」里能选到它。${t.maturity === "L3" ? "L3：按检查点设置可以自动提交。" : "每单都停在最终提交前，等人审核后才提交。"}`
    : canEnable
      ? `未启用：文员看不到这个类型。成熟度已是 ${t.maturity}，点右上角「启用」文员就能建任务。`
      : "未启用：文员看不到这个类型。还在探索阶段，跑通后把成熟度升到 L2 再启用。";
  const rollbackGolden = async () => {
    const r = await dialog.promptEx({
      title: `回滚到黄金版本 v${t.goldenVersion}？`,
      body: <p>回滚也会生成一个新版本记录，类型会降到 L2。</p>,
      label: "为什么回滚",
      required: true,
      checkbox: "回滚后自动 dry run 一次",
      okText: "回滚",
      danger: true,
    });
    if (!r) return;
    const res = await act.run("golden", () => api.rollback(t.id, { golden: true, why: r.text, dryRun: r.checked }));
    if (res) {
      toast("ok", `已回滚，新版本 v${res.version.version}`);
      void typeQ.reload();
      if (res.dryRunTaskId) navigate(`/tasks/${res.dryRunTaskId}`);
    }
  };

  return (
    <div>
      <div className="detail-head" data-testid="detail-head">
        <Link to="/admin/types" className="back">
          ← 返回列表
        </Link>
        <div className="type-head">
          <div>
            <div className="row">
              <h1>{t.name}</h1>
              {t.clerkAdoptLocked && <Badge tone="red">文员采纳已刹车</Badge>}
            </div>
            <div className="meta">
              <span className="mono">{t.slug}</span> · SOP v{t.skillVersion} · 黄金版本 {t.goldenVersion ? `v${t.goldenVersion}` : "无"} · 30 天通过率{" "}
              {fmtPct(t.stats.passRate30d)} · 30 天运行 {t.stats.runs30d} 次
            </div>
          </div>
          <div className="type-head-actions">
            <label className="row small">
              <span className="muted">成熟度</span>
              <select value={t.maturity} disabled={!!act.busy} onChange={(e) => void setMaturity(e.target.value as Maturity)} aria-label="成熟度" data-testid="maturity-select">
                {(["L0", "L1", "L2", "L3"] as Maturity[]).map((m) => (
                  <option key={m} value={m}>{MATURITY_TEXT[m]}</option>
                ))}
              </select>
            </label>
            {t.enabled ? (
              <button className="btn" disabled={!!act.busy} onClick={() => void setEnabled(false)} data-testid="disable-type">
                停用
              </button>
            ) : (
              <button className="btn btn-primary" disabled={!!act.busy || !canEnable} title={canEnable ? "" : "成熟度到 L2 才能启用"} onClick={() => void setEnabled(true)} data-testid="enable-type">
                ✓ 启用
              </button>
            )}
          </div>
        </div>
        <div className={`type-state ${t.enabled ? "on" : "off"}`} data-testid="type-state">
          {t.enabled ? <Badge tone="green">已启用</Badge> : <Badge tone="gray">未启用</Badge>}
          <span>{state}</span>
        </div>
        {act.error && (
          <div className="notice err" style={{ marginTop: 10, marginBottom: 0 }}>
            {act.error}
          </div>
        )}
        {(t.learningMode && !t.enabled) || t.testManifest || t.goldenVersion ? (
          <div className="row type-ops">
            {t.learningMode && !t.enabled && (
              <>
                <button className="btn" disabled={!!act.busy} title="只需目标描述，SOP、字段和文件清单由实际运行逐步整理；到提交前就结束，不会提交" onClick={() => void firstRun(false)}>开始探索（演练，不提交）</button>
                <button className="btn" disabled={!!act.busy || !t.checkpoints.length} title={t.checkpoints.length ? "到提交前停在检查点，审核批准后才提交" : "先在设置里配置提交前检查点"} onClick={() => void firstRun(true)}>正式探索（提交前审核）</button>
              </>
            )}
            {t.testManifest && (
              <button className="btn" disabled={!!act.busy} onClick={() => void dryRun()}>
                ▶ dry run 一次
              </button>
            )}
            {t.goldenVersion && (
              <button className="btn btn-danger" disabled={!!act.busy} onClick={() => void rollbackGolden()}>
                ⟲ 回滚到黄金版本
              </button>
            )}
          </div>
        ) : null}
        <div className="tabs">
          {TABS.map(([k, label]) => (
            <button key={k} type="button" className={tab === k ? "active" : ""} onClick={() => setTab(k)} aria-pressed={tab === k}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {tab === "settings" && <SettingsTab t={t} onSaved={() => void typeQ.reload()} />}
      {tab === "supp" && <div className="card"><SopSupplements typeId={t.id} /></div>}
      {tab === "sop" && <SopTab t={t} onSaved={() => void typeQ.reload()} />}
      {tab === "memory" && <MemoryTab t={t} />}
      {tab === "versions" && <VersionsTab t={t} onChanged={() => void typeQ.reload()} />}
      {tab === "timeline" && <TimelineTab typeId={t.id} />}
      {tab === "ai" && <AiTab typeId={t.id} />}
    </div>
  );
}

/* ---------------- 操作记忆 ---------------- */

function MemoryTab({ t }: { t: TypeDetail }) {
  const { toast } = useAlerts();
  const q = useLoad(() => api.typeMemory(t.id), [t.id]);
  const [content, setContent] = useState("");
  const [why, setWhy] = useState("");
  const act = useAction();
  useEffect(() => {
    if (q.data) setContent(q.data.content);
  }, [q.data]);
  const dirty = !!q.data && content !== q.data.content;
  const save = async () => {
    if (!why.trim()) {
      act.setError("请填写「为什么改」");
      return;
    }
    const r = await act.run("save", () => api.saveTypeMemory(t.id, content, why.trim()));
    if (r) {
      toast("ok", "操作记忆已保存");
      setWhy("");
      void q.reload();
    }
  };
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0, flex: 1 }}>MEMORY.md</h2>
        <span className="small muted mono">skills/{t.slug}/</span>
      </div>
      <p className="small muted">这个操作的工作目录里除了 SOP，还有这份记忆：每次运行开始先读，结束时自动追加本次实测的页面、选项、等待时间、坑和人工指示（已去除个人资料）。可以在这里合并整理。</p>
      {q.error && <div className="notice err">{q.error}</div>}
      <textarea className="code" aria-label="操作记忆" value={content} onChange={(e) => setContent(e.target.value)} spellCheck={false} />
      <div className="row" style={{ marginTop: 12 }}>
        <input style={{ flex: 1, minWidth: 260 }} placeholder="为什么改（必填，会记入 git 历史）" aria-label="为什么改记忆" value={why} onChange={(e) => setWhy(e.target.value)} />
        <button className="btn btn-primary" disabled={!dirty || !!act.busy} onClick={() => void save()}>
          保存记忆
        </button>
      </div>
      {act.error && <div className="notice err" style={{ marginTop: 10 }}>{act.error}</div>}
    </div>
  );
}

/* ---------------- 设置 ---------------- */

function SettingsTab({ t, onSaved }: { t: TypeDetail; onSaved: () => void }) {
  const { toast } = useAlerts();
  const act = useAction();
  const [f, setF] = useState(() => ({
    name: t.name,
    description: t.description,
    category: t.category as string,
    isQuery: t.isQuery,
    stepsExpected: t.stepsExpected,
    entryUrl: t.entryUrl ?? "",
    browserKind: (t.browserKind ?? "any") as BrowserKind,
    runtime: { ...t.runtime },
    manifestSchema: t.manifestSchema,
    checkpoints: t.checkpoints.map((c) => ({ ...c, evidence: { ...c.evidence, fieldsToCompare: [...c.evidence.fieldsToCompare] } })),
    completion: {
      screenshotRequired: t.completionEvidence.screenshotRequired,
      whatToCapture: t.completionEvidence.whatToCapture.join("\n"),
      receiptNumberRegex: t.completionEvidence.receiptNumberRegex ?? "",
    },
    testManifest: t.testManifest ? JSON.stringify(t.testManifest, null, 2) : "",
  }));
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((s) => ({ ...s, [k]: v }));
  const fieldKeys = ["companyName", "uen", ...f.manifestSchema.fields.map((x) => x.key).filter(Boolean)];

  const save = async () => {
    // 探索类型必须有开始网址（开始申请时打开哪个网址）
    if (t.learningMode && !f.entryUrl.trim()) { act.setError("请填写开始网址"); return; }
    let testManifest: unknown = null;
    if (f.testManifest.trim()) {
      try {
        testManifest = JSON.parse(f.testManifest);
      } catch (e) {
        act.setError(`测试 manifest 不是合法 JSON：${(e as Error).message}`);
        return;
      }
    }
    const schema = cleanSchema(f.manifestSchema);
    const schemaChanged = JSON.stringify(schema) !== JSON.stringify(cleanSchema(t.manifestSchema));
    const body: Record<string, unknown> = {
      name: f.name,
      description: f.description,
      category: f.category,
      isQuery: f.isQuery,
      stepsExpected: f.stepsExpected,
      entryUrl: f.entryUrl.trim() || null,
      browserKind: f.browserKind,
      runtime: f.runtime,
      manifestSchema: schemaChanged ? { ...schema, version: schema.version + 1 } : schema,
      checkpoints: f.checkpoints,
      completionEvidence: {
        screenshotRequired: f.completion.screenshotRequired,
        whatToCapture: f.completion.whatToCapture.split("\n").map((s) => s.trim()).filter(Boolean),
        receiptNumberRegex: f.completion.receiptNumberRegex.trim() || null,
      },
      testManifest,
    };
    const r = await act.run("save", () => api.updateType(t.id, body));
    if (r) {
      toast("ok", "已保存");
      onSaved();
    }
  };

  const setCp = (i: number, patch: Partial<CheckpointDef>) => set("checkpoints", f.checkpoints.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const setEv = (i: number, patch: Partial<CheckpointDef["evidence"]>) => {
    const c = f.checkpoints[i];
    if (c) setCp(i, { evidence: { ...c.evidence, ...patch } });
  };

  return (
    <div>
      <div className="card">
        <h2>基本</h2>
        <div className="form-grid">
          <label className="field">
            <span className="lbl">名称</span>
            <input value={f.name} onChange={(e) => set("name", e.target.value)} />
          </label>
          <label className="field">
            <span className="lbl">分类</span>
            <CategoryPicker value={f.category} onChange={(v) => set("category", v)} />
          </label>
          <label className="field">
            <span className="lbl">预计步骤数</span>
            <input type="number" min={1} value={f.stepsExpected} onChange={(e) => set("stepsExpected", Math.max(1, Number(e.target.value) || 1))} />
          </label>
        </div>
        <label className="field">
          <span className="lbl">描述</span>
          <textarea value={f.description} onChange={(e) => set("description", e.target.value)} />
        </label>
        <div className="form-grid">
          <label className="field">
            <span className="lbl">开始网址<span className="req">*</span>（开始申请时打开的网址；任务建好就在浏览器里打开它待命）</span>
            <input aria-label="开始网址" value={f.entryUrl} placeholder="例如 https://www.bizfile.gov.sg/" onChange={(e) => set("entryUrl", e.target.value)} />
          </label>
          <label className="field">
            <span className="lbl">用哪种 Chrome</span>
            <select aria-label="用哪种 Chrome" value={f.browserKind} onChange={(e) => set("browserKind", e.target.value as BrowserKind)}>
              <option value="corppass">Corppass 个人的（建任务时选谁来扫码）</option>
              <option value="ica">ICA 专用</option>
              <option value="any">通用</option>
            </select>
          </label>
        </div>
        <div className="row">
          <label className="check">
            <input type="checkbox" checked={f.isQuery} onChange={(e) => set("isQuery", e.target.checked)} />
            查询类
          </label>
        </div>
      </div>
      <div className="card">
        <h2>运行时限</h2>
        <div className="form-grid">
          {(
            [
              ["expectedMinutes", "预计分钟"],
              ["maxMinutes", "最长分钟"],
              ["retryBudget", "重试次数"],
              ["stuckTimeoutMinutes", "无进展超时（分钟）"],
            ] as const
          ).map(([k, label]) => (
            <label className="field" key={k}>
              <span className="lbl">{label}</span>
              <input type="number" min={0} value={f.runtime[k]} onChange={(e) => set("runtime", { ...f.runtime, [k]: Number(e.target.value) })} />
            </label>
          ))}
        </div>
      </div>
      <div className="card">
        <h2>任务资料（manifest）</h2>
        <SchemaEditor value={f.manifestSchema} onChange={(v) => set("manifestSchema", v)} />
      </div>
      <div className="card">
        <h2>检查点</h2>
        {f.checkpoints.map((c, i) => (
          <div key={i} className="upload-item" data-testid="checkpoint">
            <div className="form-grid">
              <label className="field">
                <span className="lbl">id</span>
                <input value={c.id} onChange={(e) => setCp(i, { id: e.target.value })} />
              </label>
              <label className="field">
                <span className="lbl">触发（停在哪）</span>
                <input value={c.trigger} onChange={(e) => setCp(i, { trigger: e.target.value })} />
              </label>
              <label className="field">
                <span className="lbl">审核人</span>
                <select value={c.reviewer} onChange={(e) => setCp(i, { reviewer: e.target.value as CheckpointDef["reviewer"] })}>
                  {Object.entries(REVIEWER_TEXT).map(([v, t2]) => (
                    <option key={v} value={v}>{t2}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="lbl">超时（分钟）</span>
                <input type="number" min={1} value={c.timeoutMinutes} onChange={(e) => setCp(i, { timeoutMinutes: Math.max(1, Number(e.target.value) || 1) })} />
              </label>
              <label className="field">
                <span className="lbl">超时后</span>
                <select value={c.timeoutAction} onChange={(e) => setCp(i, { timeoutAction: e.target.value as CheckpointDef["timeoutAction"] })}>
                  <option value="remind">再提醒</option>
                  <option value="block">转卡住</option>
                </select>
              </label>
            </div>
            <div className="lbl" style={{ fontWeight: 600 }}>比对字段</div>
            <div className="row" style={{ margin: "6px 0 10px" }}>
              {fieldKeys.map((k) => (
                <label key={k} className="check small">
                  <input
                    type="checkbox"
                    checked={c.evidence.fieldsToCompare.includes(k)}
                    onChange={(e) =>
                      setEv(i, {
                        fieldsToCompare: e.target.checked ? [...c.evidence.fieldsToCompare, k] : c.evidence.fieldsToCompare.filter((x) => x !== k),
                      })
                    }
                  />
                  {k}
                </label>
              ))}
            </div>
            <div className="row">
              <label className="check">
                <input type="checkbox" checked={c.evidence.fullpageScreenshot} onChange={(e) => setEv(i, { fullpageScreenshot: e.target.checked })} />
                整页截图
              </label>
              <label className="check">
                <input type="checkbox" checked={c.evidence.showFee} onChange={(e) => setEv(i, { showFee: e.target.checked })} />
                显示费用
              </label>
              <label className="check" title={t.maturity !== "L3" ? "只有 L3 可以打开自动提交" : ""}>
                <input type="checkbox" checked={c.autoSubmit} disabled={t.maturity !== "L3"} onChange={(e) => setCp(i, { autoSubmit: e.target.checked })} />
                自动提交（仅 L3）
              </label>
              <span className="spacer" />
              <button className="btn btn-small btn-danger" onClick={() => set("checkpoints", f.checkpoints.filter((_, j) => j !== i))}>
                删除检查点
              </button>
            </div>
          </div>
        ))}
        <button
          className="btn btn-small"
          onClick={() =>
            set("checkpoints", [
              ...f.checkpoints,
              { id: "submit", trigger: "", reviewer: "either", evidence: { fullpageScreenshot: true, fieldsToCompare: ["companyName", "uen"], showFee: true }, timeoutMinutes: 60, timeoutAction: "remind", autoSubmit: false },
            ])
          }
        >
          ＋ 加检查点
        </button>
      </div>
      <div className="card">
        <h2>完成证据</h2>
        <label className="check">
          <input type="checkbox" checked={f.completion.screenshotRequired} onChange={(e) => set("completion", { ...f.completion, screenshotRequired: e.target.checked })} />
          必须截图
        </label>
        <label className="field" style={{ marginTop: 10 }}>
          <span className="lbl">截什么（一行一项）</span>
          <textarea value={f.completion.whatToCapture} onChange={(e) => set("completion", { ...f.completion, whatToCapture: e.target.value })} />
        </label>
        <label className="field">
          <span className="lbl">回执号格式（正则）</span>
          <input className="mono" value={f.completion.receiptNumberRegex} onChange={(e) => set("completion", { ...f.completion, receiptNumberRegex: e.target.value })} />
        </label>
      </div>
      <div className="card">
        <h2>测试 manifest（dry run / 对比验证用）</h2>
        <textarea
          className="code"
          style={{ minHeight: 180 }}
          value={f.testManifest}
          placeholder={'{"clientEntity":{"name":"…","uen":"…"},"fields":{}}'}
          onChange={(e) => set("testManifest", e.target.value)}
        />
      </div>
      {act.error && <div className="notice err">{act.error}</div>}
      <div className="row end" style={{ marginBottom: 40 }}>
        <button className="btn btn-primary btn-big" disabled={!!act.busy} onClick={() => void save()}>
          {act.busy ? "保存中…" : "保存设置"}
        </button>
      </div>
    </div>
  );
}

/* ---------------- SOP ---------------- */

function SopTab({ t, onSaved }: { t: TypeDetail; onSaved: () => void }) {
  const { toast } = useAlerts();
  const q = useLoad(() => api.skill(t.id), [t.id]);
  const [content, setContent] = useState("");
  const [why, setWhy] = useState("");
  const [bump, setBump] = useState<"patch" | "minor">("minor");
  const act = useAction();
  useEffect(() => {
    if (q.data) setContent(q.data.content ?? "");
  }, [q.data]);
  const dirty = !!q.data && content !== (q.data.content ?? "");
  const save = async () => {
    if (!why.trim()) {
      act.setError("请填写「为什么改」");
      return;
    }
    const r = await act.run("save", () => api.editSkill(t.id, content, why.trim(), bump));
    if (r) {
      toast("ok", `已保存为 v${r.version}`);
      setWhy("");
      void q.reload();
      onSaved();
    }
  };
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0, flex: 1 }}>SKILL.md</h2>
        <span className="small muted mono">v{q.data?.version} · {q.data?.commit?.slice(0, 7)}</span>
      </div>
      {q.error && <div className="notice err">{q.error}</div>}
      <textarea className="code" aria-label="SOP 内容" value={content} onChange={(e) => setContent(e.target.value)} spellCheck={false} />
      <div className="row" style={{ marginTop: 12 }}>
        <input style={{ flex: 1, minWidth: 260 }} placeholder="为什么改（必填，会记入版本历史）" aria-label="为什么改" value={why} onChange={(e) => setWhy(e.target.value)} />
        <select style={{ width: "auto" }} value={bump} onChange={(e) => setBump(e.target.value as "patch" | "minor")} aria-label="版本号">
          <option value="minor">次版本 +1（较大改动）</option>
          <option value="patch">补丁号 +1（小改）</option>
        </select>
        <button className="btn btn-primary" disabled={!dirty || !!act.busy} onClick={() => void save()}>
          保存 SOP
        </button>
      </div>
      {act.error && <div className="notice err" style={{ marginTop: 10 }}>{act.error}</div>}
      <p className="small muted">保存后版本号 +1；L3 类型会自动降到 L2，需要重新积累成熟度。</p>
    </div>
  );
}

/* ---------------- 版本历史 ---------------- */

const KIND_TEXT: Record<SkillVersionDTO["kind"], string> = { create: "新建", adopt: "采纳提议", "admin-edit": "管理员修改", rollback: "回滚" };

function VersionsTab({ t, onChanged }: { t: TypeDetail; onChanged: () => void }) {
  const navigate = useNavigate();
  const dialog = useDialog();
  const { toast } = useAlerts();
  const q = useLoad(() => api.versions(t.id), [t.id, t.skillVersion]);
  const [open, setOpen] = useState<string | null>(null);
  const [hidden, toggleCol] = useHiddenColumns("csp-hidden-columns-versions");
  const act = useAction();

  const rollback = async (v: SkillVersionDTO) => {
    const r = await dialog.promptEx({
      title: `回滚到 v${v.version}？`,
      body: <p>会把 SOP 内容恢复成 v{v.version}，并生成一个新版本记录。</p>,
      label: "为什么回滚",
      required: true,
      checkbox: "回滚后自动 dry run 一次",
      okText: "回滚",
      danger: true,
    });
    if (!r) return;
    const res = await act.run(`rb-${v.id}`, () => api.rollback(t.id, { versionId: v.id, why: r.text, dryRun: r.checked }));
    if (res) {
      toast("ok", `已回滚，新版本 v${res.version.version}`);
      void q.reload();
      onChanged();
      if (res.dryRunTaskId) navigate(`/tasks/${res.dryRunTaskId}`);
    }
  };
  const compare = async (v: SkillVersionDTO) => {
    if (!t.testManifest) {
      act.setError("请先在设置里配置测试 manifest");
      return;
    }
    const ok = await dialog.confirm({ title: `对比验证 v${v.version} 与当前版？`, body: <p>会用测试 manifest 各 dry run 一次（不提交），然后逐步对比。</p>, okText: "开始对比" });
    if (!ok) return;
    const r = await act.run(`cmp-${v.id}`, () => api.compare(t.id, v.id));
    if (r) navigate(`/admin/compare/${r.group}`);
  };

  const rows = q.data ?? [];
  const currentId = rows[0]?.id;
  const openV = rows.find((v) => v.id === open) ?? null;
  const columns: Column<SkillVersionDTO>[] = [
    {
      key: "version",
      label: "版本",
      fixed: true,
      render: (v) => (
        <div className="cell-two">
          <b>
            v{v.version}
            {v.id === currentId && (
              <>
                {" "}
                <Badge tone="green">当前</Badge>
              </>
            )}
            {t.goldenVersion === v.version && (
              <>
                {" "}
                <Badge tone="amber">黄金</Badge>
              </>
            )}
          </b>
          <span className="mono">{v.commit.slice(0, 7)}</span>
        </div>
      ),
    },
    { key: "kind", label: "类型", render: (v) => KIND_TEXT[v.kind] },
    { key: "who", label: "谁", render: (v) => v.who },
    {
      key: "why",
      label: "为什么",
      fixed: true,
      render: (v) => (
        <div className="cell-two">
          <b style={{ fontWeight: 400, maxWidth: 360 }} title={v.why}>
            {v.why}
          </b>
          {v.sourceRunId && <span>来源运行 {v.sourceRunId.slice(-6)}</span>}
        </div>
      ),
    },
    { key: "at", label: "时间", render: (v) => <span title={fmtDateTime(v.createdAt)}>{fmtTime(v.createdAt)}</span> },
    {
      key: "op",
      label: "操作",
      fixed: true,
      render: (v) => (
        <div className="row">
          {v.diff && (
            <button className="btn btn-small" onClick={() => setOpen(open === v.id ? null : v.id)}>
              {open === v.id ? "收起" : "看改动"}
            </button>
          )}
          {v.id !== currentId && (
            <>
              <button className="btn btn-small btn-danger" disabled={!!act.busy} onClick={() => void rollback(v)}>
                回滚到此版本
              </button>
              <button className="btn btn-small" disabled={!!act.busy} onClick={() => void compare(v)}>
                与当前版对比验证
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="card">
        <h2>版本历史</h2>
        <div className="list-bar">
          <span className="count">共 {rows.length} 个版本 · 从新到旧，第一行是当前版本 · 回滚也会生成一个新版本</span>
          <ColumnPicker columns={columns} hidden={hidden} onToggle={toggleCol} />
        </div>
        {act.error && <div className="notice err">{act.error}</div>}
        {q.error && <div className="notice err">{q.error}</div>}
        {q.loading && !q.data ? (
          <div className="empty">正在加载…</div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(v) => v.id}
            hidden={hidden}
            rowClass={(v) => (v.id === open ? "selected" : undefined)}
            rowAttrs={(v) => ({ "data-testid": "version-row", "data-version": v.version })}
            empty="还没有版本记录"
          />
        )}
      </div>
      {openV && (
        <div className="card" data-testid="version-diff">
          <div className="row" style={{ marginBottom: 10 }}>
            <h2 style={{ margin: 0, flex: 1 }}>
              v{openV.version} 的改动 <span className="small muted">（{openV.why}）</span>
            </h2>
            <button className="btn btn-small" onClick={() => setOpen(null)}>
              收起改动
            </button>
          </div>
          <DiffView diff={openV.diff} maxHeight={400} />
        </div>
      )}
    </>
  );
}

/* ---------------- 时间线 ---------------- */

const MARKER_COLUMNS: Column<MarkerItem>[] = [
  { key: "at", label: "时间", fixed: true, render: (m) => <span title={fmtDateTime(m.at)}>{fmtTime(m.at)}</span> },
  { key: "kind", label: "种类", fixed: true, render: (m) => <MarkerBadge kind={m.kind} /> },
  {
    key: "text",
    label: "说明",
    fixed: true,
    render: (m) => (
      <span className="cell-clip" style={{ maxWidth: 520 }} title={m.text}>
        {m.text}
      </span>
    ),
  },
  {
    key: "change",
    label: "变化",
    render: (m) =>
      m.from || m.to ? (
        <span className="mono">
          {m.from ?? "—"} → {m.to ?? "—"}
        </span>
      ) : (
        <span className="muted">—</span>
      ),
  },
];

function TimelineTab({ typeId }: { typeId: string }) {
  const navigate = useNavigate();
  const [days, setDays] = useState(90);
  const [hidden, toggleCol] = useHiddenColumns("csp-hidden-columns-type-markers");
  const q = useLoad(() => api.typeTimeline(typeId, days), [typeId, days]);
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0, flex: 1 }}>时间线</h2>
        <select style={{ width: "auto" }} value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="时间范围">
          <option value={30}>最近 30 天</option>
          <option value={90}>最近 90 天</option>
          <option value={365}>最近一年</option>
        </select>
      </div>
      {q.error && <div className="notice err">{q.error}</div>}
      {q.data && (
        <>
          <TimelineChart runs={q.data.runs} markers={q.data.markers} days={days} onRunClick={(r) => navigate(`/tasks/${r.taskId}`)} />
          <div className="list-bar" style={{ marginTop: 18 }}>
            <span className="count">
              <b style={{ color: "var(--fg)" }}>变更标记</b> · 共 {q.data.markers.length} 条 · 从新到旧
            </span>
            <ColumnPicker columns={MARKER_COLUMNS} hidden={hidden} onToggle={toggleCol} />
          </div>
          <DataTable columns={MARKER_COLUMNS} rows={[...q.data.markers].reverse()} rowKey={(m) => m.id} hidden={hidden} empty="这段时间没有变更" />
        </>
      )}
    </div>
  );
}

/* ---------------- AI 分析 ---------------- */

function AiTab({ typeId }: { typeId: string }) {
  const q = useLoad(() => api.analyses(typeId), [typeId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const analyze = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.analyze(typeId);
      await q.reload();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0, flex: 1 }}>AI 分析</h2>
        <button className="btn btn-primary" disabled={busy} onClick={() => void analyze()}>
          {busy ? "AI 分析中，可能要几十秒…" : "🤖 让 AI 分析"}
        </button>
      </div>
      <p className="small muted">只读分析：AI 看运行账本、变更标记和最近运行，写出疑似原因，不会改任何东西。</p>
      {error && <div className="notice err">{error}</div>}
      {q.data?.length === 0 && <div className="empty">还没有分析记录，点右上角「让 AI 分析」</div>}
      {q.data?.map((a) => (
        <div key={a.id} className="upload-item" data-testid="analysis">
          <div className="small muted">
            <span title={fmtDateTime(a.createdAt)}>{fmtTime(a.createdAt)}</span> · {a.trigger}
          </div>
          <div className="pre" translate="no" style={{ marginTop: 6 }}>{a.text}</div>
        </div>
      ))}
    </div>
  );
}
